import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  getGetWorkspaceConfigurationQueryKey,
  useValidateWorkspaceConfiguration
} from '@r/api/generated/client.ts';

export type ConfigurationScope = 'domain' | 'email';

/*
 * Demande à Vercel et Brevo de revérifier les enregistrements DNS : le workspace passe en actif si tout est valide,
 * en mal configuré sinon. La configuration retournée remplace celle en cache, et le toast porte sur la configuration
 * de la page (nom de domaine ou e-mail).
 */
export const useWorkspaceConfigurationValidation = (
  workspaceId: number | undefined,
  scope: ConfigurationScope
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const mutation = useValidateWorkspaceConfiguration({
    mutation: {
      onSuccess: async (configuration) => {
        queryClient.setQueryData(getGetWorkspaceConfigurationQueryKey(workspaceId), configuration);
        // Le statut du workspace a pu changer (bandeau, badge)
        await queryClient.invalidateQueries({ queryKey: ['workspace'] });

        const isDomainValid = configuration.dnsConfiguration.isValid;
        const isEmailValid = configuration.emailConfiguration.isValid;
        const isScopeValid = scope === 'domain' ? isDomainValid : isEmailValid;
        const isOtherValid = scope === 'domain' ? isEmailValid : isDomainValid;

        if (!isScopeValid) {
          toast.warning(t(`workspace.configuration.validate.${scope}.invalid`));
          return;
        }

        // L'autre configuration empêche encore le workspace d'être actif
        toast.success(t(`workspace.configuration.validate.${scope}.success`), {
          description: isOtherValid
            ? undefined
            : t(
                `workspace.configuration.validate.remaining.${scope === 'domain' ? 'email' : 'domain'}`
              )
        });
      },
      onError: () => {
        toast.error(t('workspace.configuration.validate.error'));
      }
    }
  });

  return {
    validate: () => mutation.mutate({ id: workspaceId, data: {} }),
    isValidating: mutation.isPending
  };
};
